
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BellRing, Link, CheckCircle, XCircle } from 'lucide-react';
import { StoreIntegrationConfig } from '@/types/merchandise';

const StoreIntegrations = () => {
  // Mock data for available integrations
  const integrations: StoreIntegrationConfig[] = [
    {
      platform: 'shopify',
      name: 'Shopify',
      description: 'Connect your Shopify store to sync products, inventory, and orders.',
      logo: 'https://cdn.shopify.com/s/files/1/0070/7032/files/shopify_logo_whitebg.png',
      isConfigured: true,
      connectionInstructions: [
        'Create a custom app in your Shopify admin',
        'Generate API credentials',
        'Enter your shop domain and API key below'
      ]
    },
    {
      platform: 'woocommerce',
      name: 'WooCommerce',
      description: 'Integrate with your WordPress WooCommerce store.',
      logo: 'https://woocommerce.com/wp-content/uploads/2011/12/wc-icon-purple-512x512.png',
      isConfigured: false,
      connectionInstructions: [
        'Generate REST API keys in your WooCommerce settings',
        'Ensure your WordPress site is publicly accessible',
        'Enter your site URL, consumer key, and consumer secret below'
      ]
    }
  ];

  const handleConnect = (platform: string) => {
    console.log(`Connecting to ${platform}`);
    // This would open a configuration modal or redirect to OAuth flow
  };

  const handleDisconnect = (platform: string) => {
    console.log(`Disconnecting from ${platform}`);
    // This would disconnect the integration
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrations.map((integration) => (
          <Card key={integration.platform} className="overflow-hidden">
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle>{integration.name}</CardTitle>
                <CardDescription>{integration.description}</CardDescription>
              </div>
              <div className="h-12 w-12 overflow-hidden">
                <img 
                  src={integration.logo} 
                  alt={`${integration.name} logo`}
                  className="h-full w-full object-contain"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2 mb-4">
                <div className={`p-1 rounded-full ${integration.isConfigured ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {integration.isConfigured ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <BellRing className="h-4 w-4 text-amber-600" />
                  )}
                </div>
                <span className={integration.isConfigured ? 'text-green-600' : 'text-amber-600'}>
                  {integration.isConfigured ? 'Connected' : 'Not connected'}
                </span>
              </div>

              {!integration.isConfigured && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Connection Instructions:</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    {integration.connectionInstructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="mt-4">
                {integration.isConfigured ? (
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Link className="h-4 w-4 mr-2" />
                      Visit Store
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleDisconnect(integration.platform)}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button 
                    className="w-full" 
                    onClick={() => handleConnect(integration.platform)}
                  >
                    Connect {integration.name}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default StoreIntegrations;
